import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/feature/SEO';

const siteUrl = import.meta.env.VITE_SITE_URL ?? '';

const servicesJsonLd = [
  {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: 'Home Accessibility Services - SafeStay Solutions Beverly MA',
    description:
      'Comprehensive home accessibility and structural safety services in Beverly, MA: grab bars, stair lifts, wheelchair ramps, doorway widening, motion lighting, and kitchen modifications.',
    url: `${siteUrl}/services`,
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${siteUrl}/` },
        { '@type': 'ListItem', position: 2, name: 'Services', item: `${siteUrl}/services` },
      ],
    },
  },
  {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'SafeStay Home Safety Services',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        item: {
          '@type': 'Service',
          name: 'Bathroom Safety Modifications',
          description: 'Walk-in tubs, grab bars, non-slip flooring, and accessible showers.',
          provider: { '@type': 'LocalBusiness', name: 'SafeStay Solutions' },
        },
      },
      {
        '@type': 'ListItem',
        position: 2,
        item: {
          '@type': 'Service',
          name: 'Mobility Access Solutions',
          description: 'Wheelchair ramps, stair lifts, platform lifts, and handrail installation.',
          provider: { '@type': 'LocalBusiness', name: 'SafeStay Solutions' },
        },
      },
      {
        '@type': 'ListItem',
        position: 3,
        item: {
          '@type': 'Service',
          name: 'Doorway Widening',
          description: 'Doorway expansion, threshold removal, and swing-clear hinge installation.',
          provider: { '@type': 'LocalBusiness', name: 'SafeStay Solutions' },
        },
      },
      {
        '@type': 'ListItem',
        position: 4,
        item: {
          '@type': 'Service',
          name: 'Lighting & Visibility Upgrades',
          description: 'Motion-sensor lighting, under-cabinet lighting, and stairway lighting.',
          provider: { '@type': 'LocalBusiness', name: 'SafeStay Solutions' },
        },
      },
      {
        '@type': 'ListItem',
        position: 5,
        item: {
          '@type': 'Service',
          name: 'Kitchen Modifications',
          description: 'Lowered countertops, pull-out shelving, lever faucets, and accessible hardware.',
          provider: { '@type': 'LocalBusiness', name: 'SafeStay Solutions' },
        },
      },
      {
        '@type': 'ListItem',
        position: 6,
        item: {
          '@type': 'Service',
          name: 'Custom Home Safety Solutions',
          description: 'Personalized accessibility assessments and custom modifications.',
          provider: { '@type': 'LocalBusiness', name: 'SafeStay Solutions' },
        },
      },
    ],
  },
];

export default function ServicesPage() {
  const navigate = useNavigate();

  const services = [
    {
      icon: 'ri-bowl-line',
      title: 'Bathroom Safety',
      description: 'Walk-in tubs, grab bars, non-slip flooring, and accessible showers designed for maximum safety and independence.',
      features: [
        'Professional grab bar installation in showers, tubs, and near toilets',
        'Walk-in tub conversions for easy, safe bathing',
        'Non-slip flooring installation to prevent falls',
        'Accessible shower conversions with zero-threshold entry',
        'Raised toilet seats and safety frames',
        'Handheld shower head installations'
      ]
    },
    {
      icon: 'ri-stairs-line',
      title: 'Mobility Access',
      description: 'Professional installation of stair lifts, wheelchair ramps, and platform lifts ensuring smooth transitions between levels.',
      features: [
        'Custom wheelchair ramp design and installation',
        'Stair lift installation for straight and curved staircases',
        'Platform lift solutions for multi-level access',
        'Threshold ramp installation for doorways',
        'Handrail installation along hallways and stairs',
        'Vertical platform lifts for porches and decks'
      ]
    },
    {
      icon: 'ri-door-open-line',
      title: 'Doorway Widening',
      description: 'Expert doorway modifications and threshold removal to accommodate wheelchairs and walkers throughout your home.',
      features: [
        'Doorway expansion to 36" minimum width',
        'Threshold removal for seamless transitions',
        'Swing-clear hinge installation for extra clearance',
        'Pocket door conversions to save space',
        'Lever-style door handle replacements',
        'Automatic door opener installations'
      ]
    },
    {
      icon: 'ri-lightbulb-line',
      title: 'Lighting & Visibility',
      description: 'Enhanced lighting solutions to improve visibility and reduce fall risks in critical areas of your home.',
      features: [
        'Motion-sensor lighting in hallways and bathrooms',
        'Under-cabinet lighting in kitchens',
        'Stairway lighting with automatic controls',
        'Night lights in bedrooms and bathrooms',
        'Improved switch accessibility and placement',
        'Emergency backup lighting systems'
      ]
    },
    {
      icon: 'ri-home-heart-line',
      title: 'Kitchen Modifications',
      description: 'Accessible kitchen adaptations that make cooking and daily tasks easier and safer for seniors.',
      features: [
        'Lowered countertop sections for seated work',
        'Pull-out shelving and lazy susans',
        'Lever-style faucet installations',
        'Accessible cabinet hardware',
        'Anti-scald device installations',
        'Improved task lighting'
      ]
    },
    {
      icon: 'ri-tools-line',
      title: 'Custom Solutions',
      description: 'Personalized home modifications tailored to your specific mobility needs and living situation.',
      features: [
        'Comprehensive home safety assessments',
        'Custom carpentry and structural modifications',
        'Bedroom accessibility improvements',
        'Outdoor access solutions',
        'Smart home integration for aging in place',
        'Emergency alert system installations'
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Home Accessibility Services Beverly MA - SafeStay Solutions"
        description="Expert home accessibility services in Beverly, MA: grab bar installation, stair lifts, wheelchair ramps, doorway widening, motion lighting & kitchen modifications. Licensed & insured."
        keywords="home accessibility services Beverly MA, grab bar installation, wheelchair ramps, stair lifts, doorway widening, aging in place"
        canonical={`${siteUrl}/services`}
        ogUrl={`${siteUrl}/services`}
        jsonLd={servicesJsonLd}
      />
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1A2B4A] to-[#2A3B5A] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-[#D4AF37] rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="text-[#D4AF37] text-sm font-semibold tracking-wider mb-4">OUR EXPERTISE</div>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-white mb-6 leading-tight">
            Comprehensive Home<br />Safety Solutions
          </h1>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-10 leading-relaxed">
            Expert home accessibility and structural safety modifications for seniors. From professional grab bar installation to full-home audits, we reinforce your independence.
          </p>
          <button
            onClick={() => navigate('/free-assessment')}
            className="px-8 py-4 bg-[#D4AF37] text-[#1A2B4A] rounded-full font-bold text-lg hover:bg-[#C49F2F] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
          >
            Schedule Free Assessment
          </button>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 bg-[#F8F7F5]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
            {services.map((service, index) => (
              <div
                key={index}
                className="bg-white rounded-3xl p-8 lg:p-10 shadow-sm hover:shadow-xl transition-all duration-300"
              >
                <div className="w-16 h-16 flex items-center justify-center mb-6">
                  <i className={`${service.icon} text-[#D4AF37] text-5xl`}></i>
                </div>
                <h3 className="text-[#1A2B4A] text-2xl font-bold mb-4">{service.title}</h3>
                <p className="text-gray-600 text-base leading-relaxed mb-6">{service.description}</p>
                
                <div className="space-y-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-start gap-3">
                      <div className="w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0">
                        <i className="ri-check-line text-[#D4AF37] text-lg"></i>
                      </div>
                      <span className="text-gray-700 text-sm leading-relaxed">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <div className="text-[#D4AF37] text-sm font-semibold tracking-wider mb-4">HOW IT WORKS</div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#1A2B4A] mb-6">Our Simple Process</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              From initial consultation to final installation, we make home safety modifications easy and stress-free.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                step: '01',
                title: 'Free Assessment',
                description: 'We visit your home to evaluate safety needs and discuss your specific requirements.'
              },
              {
                step: '02',
                title: 'Custom Plan or Pick a Package',
                description: (
                  <span>
                    Receive a detailed proposal with recommendations and transparent pricing, or{' '}
                    <button
                      onClick={() => navigate('/packages')}
                      className="text-[#D4AF37] hover:text-[#C49F2F] font-semibold underline cursor-pointer"
                    >
                      pick a package
                    </button>
                    .
                  </span>
                )
              },
              {
                step: '03',
                title: 'Professional Installation',
                description: 'Our licensed team completes the work efficiently, typically within 24-48 hours.'
              },
              {
                step: '04',
                title: 'Quality Check',
                description: 'Final walkthrough to ensure everything meets our high standards and your satisfaction.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#C49F2F] flex items-center justify-center">
                  <span className="text-white text-2xl font-bold">{item.step}</span>
                </div>
                <h3 className="text-[#1A2B4A] text-xl font-bold mb-3">{item.title}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {typeof item.description === 'string' ? item.description : item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-[#1A2B4A] to-[#2A3B5A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Ready to Make Your Home Safer?
          </h2>
          <p className="text-xl text-gray-300 mb-10 leading-relaxed">
            Schedule your free home safety assessment today. Our experts will evaluate your needs and provide personalized recommendations.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button
              onClick={() => navigate('/free-assessment')}
              className="px-8 py-4 bg-[#D4AF37] text-[#1A2B4A] rounded-full font-bold text-lg hover:bg-[#C49F2F] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer whitespace-nowrap"
            >
              Schedule Free Assessment
            </button>
            <a
              href="tel:6175396650"
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-bold text-lg hover:bg-white hover:text-[#1A2B4A] transition-all duration-300 cursor-pointer whitespace-nowrap"
            >
              Call (617) 539-6650
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
